import { useEffect, useState, useRef, useCallback, useContext } from "react";
import GlobalConfig from "../../context/GlobalConfig";
import styles from "../../styles/faceCapture.module.css";
import FaceCamera from "./FaceCamera";
import FaceUi from "./FaceUi";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import useMediaPipeFaceDetection from "../../hooks/useMediaPipeFaceDetection";

function FaceAutoCapture({ onPhotoTaken, onError, onMultipleFacesChange }) {
  const { config } = useContext(GlobalConfig);
  const [photoTaken, setPhotoTaken] = useState(false);
  const [multipleFacesDetected, setMultipleFacesDetected] = useState(false);
  const videoElementRef = useRef(null);
  const isMobile = useDeviceDetection();
  const debounceTimerRef = useRef(null);
  const lastDetectionTimeRef = useRef(0);
  const multipleFacesShowTimeRef = useRef(0);
  const multipleFacesDetectedRef = useRef(false);
  const singleFaceDetectionCountRef = useRef(0);

  // If config faceCaptureConfidence is true, use value from env (default 97), else use 75
  const useDynamicConfidence = config?.faceCapture?.enableFaceCaptureConfidence;
  const faceCaptureConfidence = useDynamicConfidence
    ? (parseFloat(process.env.REACT_APP_FACE_CAPTURE_CONFIDENCE) || 97)
    : 75;
  const faceConfidence = faceCaptureConfidence / 100;

  useEffect(() => {
    if (document.getElementsByClassName("sc-fubDmA")[0] !== undefined) {
      document.getElementsByClassName("sc-fubDmA")[0].style.display = "none";
    }
  });

  useEffect(() => {
    if (document.getElementsByClassName("sc-dlfmHC")[0] !== undefined) {
      document.getElementsByClassName("sc-dlfmHC")[0].style.display = "none";
    }
  });

  // Get video element from Innovatrics face capture component
  useEffect(() => {
    const findVideoElement = () => {
      const faceAutoCaptureElement = document.getElementById('x-dot-face-auto-capture');
      if (faceAutoCaptureElement) {
        const videoElement = faceAutoCaptureElement.querySelector('video');
        if (videoElement) {
          videoElementRef.current = videoElement;
          return videoElement;
        }
      }
      return null;
    };

    // Try to find video element immediately
    let videoElement = findVideoElement();

    // If not found, wait a bit and try again (component might not be mounted yet)
    if (!videoElement) {
      const timeout = setTimeout(() => {
        findVideoElement();
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, []);

  // Listen for state changes to get video element when camera is ready
  useEffect(() => {
    const handleStateChanged = (event) => {
      if (event.detail?.appState === 'RUNNING') {
        const faceAutoCaptureElement = document.getElementById('x-dot-face-auto-capture');
        if (faceAutoCaptureElement) {
          const videoElement = faceAutoCaptureElement.querySelector('video');
          if (videoElement) {
            videoElementRef.current = videoElement;
          }
        }
      }
    };

    document.addEventListener('face-auto-capture:state-changed', handleStateChanged);
    return () => {
      document.removeEventListener('face-auto-capture:state-changed', handleStateChanged);
    };
  }, []);

  // Handle multiple faces detected - pause Innovatrics with debouncing
  const handleMultipleFacesDetected = useCallback((count, detections) => {
    const now = Date.now();
    lastDetectionTimeRef.current = now;

    // Reset single face detection counter
    singleFaceDetectionCountRef.current = 0;

    // Clear any pending single face detection
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Only update if not already set
    if (!multipleFacesDetectedRef.current) {
      multipleFacesShowTimeRef.current = now;
      multipleFacesDetectedRef.current = true;
      setMultipleFacesDetected(true);
      onMultipleFacesChange?.(true); // Notify parent component

      // Pause Innovatrics face detection (if supported)
      try {
        document.dispatchEvent(
          new CustomEvent("face-auto-capture:control", {
            detail: { instruction: "pause-detection" }
          })
        );
      } catch (error) {
        // Event might not be supported, but blocking capture will still work
        console.warn('[FaceAutoCapture] Could not pause Innovatrics:', error);
      }
    }
  }, [onMultipleFacesChange]);

  // Handle single face detected - resume Innovatrics with debouncing
  const handleSingleFaceDetected = useCallback((count, detections) => {
    // Use ref to check current state (avoids stale closure)
    if (!multipleFacesDetectedRef.current) {
      singleFaceDetectionCountRef.current = 0; // Reset counter when already hidden
      return; // Already showing single face, no need to update
    }

    // Increment single face detection counter
    singleFaceDetectionCountRef.current += 1;

    const now = Date.now();
    const timeSinceLastMultiple = now - lastDetectionTimeRef.current;
    const timeSinceShowStart = now - multipleFacesShowTimeRef.current;

    // Check conditions immediately - if we have enough consecutive detections and time has passed
    if (timeSinceLastMultiple >= 1000 && timeSinceShowStart >= 1000 && singleFaceDetectionCountRef.current >= 10) {
      console.log('[FaceAutoCapture] Hiding multiple faces message - single face consistently detected');
      multipleFacesDetectedRef.current = false;
      setMultipleFacesDetected(false);
      onMultipleFacesChange?.(false); // Notify parent component
      singleFaceDetectionCountRef.current = 0; // Reset counter

      // Clear any pending timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // Resume Innovatrics face detection (if supported)
      try {
        document.dispatchEvent(
          new CustomEvent("face-auto-capture:control", {
            detail: { instruction: "continue-detection" }
          })
        );
      } catch (error) {
        // Event might not be supported, but blocking capture will still work
        console.warn('[FaceAutoCapture] Could not resume Innovatrics:', error);
      }
      return;
    }

    // If conditions not met yet, set up a fallback timer to check again
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (!multipleFacesDetectedRef.current) {
        debounceTimerRef.current = null;
        return;
      }

      const checkNow = Date.now();
      const checkTimeSinceLastMultiple = checkNow - lastDetectionTimeRef.current;
      const checkTimeSinceShowStart = checkNow - multipleFacesShowTimeRef.current;

      // Final check - if enough time has passed and we have detections, hide the message
      if (checkTimeSinceLastMultiple >= 1500 && checkTimeSinceShowStart >= 1000) {
        console.log('[FaceAutoCapture] Hiding multiple faces message - timeout reached');
        multipleFacesDetectedRef.current = false;
        setMultipleFacesDetected(false);
        onMultipleFacesChange?.(false);
        singleFaceDetectionCountRef.current = 0;

        try {
          document.dispatchEvent(
            new CustomEvent("face-auto-capture:control", {
              detail: { instruction: "continue-detection" }
            })
          );
        } catch (error) {
          console.warn('[FaceAutoCapture] Could not resume Innovatrics:', error);
        }
      }
      debounceTimerRef.current = null;
    }, 1500); // Fallback: check after 1.5 seconds
  }, [onMultipleFacesChange]);

  // Initialize MediaPipe face detection
  // Note: Hook handles errors internally, so it won't crash the app if MediaPipe fails
  useMediaPipeFaceDetection({
    videoElement: videoElementRef.current,
    enabled: !photoTaken && videoElementRef.current !== null,
    onMultipleFacesDetected: handleMultipleFacesDetected,
    onSingleFaceDetected: handleSingleFaceDetected,
  });

  const handlePhotoTaken = async (imageData, content) => {
    // Prevent capture if multiple faces are detected
    if (multipleFacesDetected) {
      console.log('[FaceAutoCapture] Blocked photo capture - multiple faces detected');
      return;
    }

    if (!photoTaken) {
      setPhotoTaken(true);
      onPhotoTaken(imageData, content);
      // handleContinueDetection();
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // const handleContinueDetection = () => {
  //   dispatchControlEvent(
  //     FaceCustomEvent.CONTROL,
  //     ControlEventInstruction.CONTINUE_DETECTION
  //   );
  // };

  return (
    <div className={styles.faceContainer}>
      <FaceCamera
        cameraFacing="user"
        onPhotoTaken={handlePhotoTaken}
        onError={onError}
        thresholds={{
          // Out of bounds threshold - lower values ensure face is more centered
          // This helps ensure user is looking directly at camera
          outOfBoundsThreshold: 0.01, // Lower threshold (0-1) - stricter centering requirement
          // Face confidence threshold - higher values ensure better face detection quality
          // The SDK's internal quality providers handle head pose (pitch/yaw) and gaze detection automatically
          // Higher confidence helps ensure the face is properly aligned and looking at camera
          faceConfidence: faceConfidence
        }}
      />
      <FaceUi
        theme={{ colors: { placeholderColorSuccess: "#00FF83" } }}
        instructions={{ candidate_selection: "Candidate selection" }}
        showCameraButtons={isMobile}
        backdropColor="rgba(255, 255, 255, 0.6)"
      />
    </div>
  );
}

export default FaceAutoCapture;
